import React, { useRef, useState } from 'react';
import { FileCheck2, Upload } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * The one file picker in the app: a dashed box you click or drop a file on.
 * Use it instead of a bare <input type="file">, whose browser-drawn button
 * cannot be styled to match.
 *
 * onSelect receives the File. The box shows the chosen file's name itself;
 * pass `file` only when the caller already keeps the file in state.
 *
 * With `multiple`, onSelect receives an array of Files instead, and the box
 * stays empty: the caller lists what was chosen (it may add to them).
 */
export default function FileDropzone({
  accept,
  onSelect,
  title = 'Choose a file',
  hint = 'or drop it here',
  file,
  compact = false,
  multiple = false,
  disabled = false,
  className,
  'data-testid': testId,
}) {
  const inputRef = useRef(null);
  const [picked, setPicked] = useState(null);
  const [dragging, setDragging] = useState(false);
  const shown = multiple ? null : file !== undefined ? file : picked;

  const choose = (fileList) => {
    const chosen = Array.from(fileList || []);
    if (!chosen.length) return;
    if (multiple) {
      onSelect?.(chosen);
      return;
    }
    setPicked(chosen[0]);
    onSelect?.(chosen[0]);
  };

  const Icon = shown ? FileCheck2 : Upload;

  return (
    <button
      type="button"
      disabled={disabled}
      data-testid={testId}
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        if (!disabled) choose(e.dataTransfer.files);
      }}
      className={cn(
        'w-full border-2 border-dashed rounded-cards text-center transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        'disabled:cursor-not-allowed disabled:opacity-50',
        compact ? 'p-4' : 'p-6',
        dragging ? 'border-ink bg-mist' : 'border-line-strong hover:bg-mist',
        className,
      )}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        tabIndex={-1}
        onChange={(e) => {
          choose(e.target.files);
          // Clear it so choosing the same file again still fires a change.
          e.target.value = '';
        }}
      />
      <Icon
        className={cn('mx-auto mb-1', compact ? 'w-6 h-6' : 'w-7 h-7', shown ? 'text-moss' : 'text-muted-foreground')}
        aria-hidden="true"
      />
      {shown ? (
        <>
          <p className={cn('font-medium truncate', compact ? 'text-xs' : 'text-sm')}>{shown.name}</p>
          <p className="text-xs text-muted-foreground">Click to choose a different file</p>
        </>
      ) : (
        <>
          <p className={cn(compact ? 'text-xs text-muted-foreground' : 'text-sm font-medium')}>{title}</p>
          {hint && !compact && <p className="text-xs text-muted-foreground">{hint}</p>}
        </>
      )}
    </button>
  );
}
